import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { tenantService } from "../services";
import { toast } from "react-toastify";
import MaxAuthBrandPanel from "../components/auth/MaxAuthBrandPanel";
import MaxAuthMobileBar from "../components/auth/MaxAuthMobileBar";
import "../styles/max-auth-pages.css";

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    tenantCode: "",
    tenantName: "",
    contactPerson: "",
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  useEffect(() => {
    fetchNextTenantCode();
  }, []);

  const fetchNextTenantCode = async () => {
    try {
      const response = await tenantService.getNextTenantCode();
      if (response && response.code) {
        setFormData((prev) => ({ ...prev, tenantCode: response.code }));
      }
    } catch (err) {
      console.error("Error fetching next tenant code:", err);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.tenantName.trim()) {
      newErrors.tenantName = "Company name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const registrationData = {
        tenantCode: formData.tenantCode,
        tenantName: formData.tenantName.trim(),
        contactPerson: formData.contactPerson.trim() || null,
        email: formData.email.trim(),
        phone: formData.phone.trim() || null,
      };

      const response = await tenantService.registerTenant(registrationData);

      localStorage.setItem("pendingTenantId", response.tenantId.toString());
      if (response.adminUsername) {
        localStorage.setItem("pendingUsername", response.adminUsername);
      }

      const welcomeText =
        response.message ||
        `Welcome! We've sent your administrator password to ${registrationData.email.trim()}. Please check your email, then sign in with your tenant code as your username.`;

      toast.success(
        "Welcome! We've sent your sign-in password to your email. Redirecting to sign in…",
        { autoClose: 6000 },
      );

      navigate("/login", {
        replace: true,
        state: {
          message: welcomeText,
          username: response.adminUsername,
        },
      });
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.errors ||
        err.message ||
        "Registration failed. Please try again.";

      if (typeof errorMessage === "object") {
        setErrors(errorMessage);
        toast.error("Registration failed. Please check the form.");
      } else {
        setErrors({ submit: errorMessage });
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const cardVariants = {
    hidden: { y: 12, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <div className="max-auth-page">
      <MaxAuthMobileBar />
      <div className="max-auth-layout">
        <MaxAuthBrandPanel variant="register" />

        <div className="max-auth-form-side">
          <div className="max-auth-form-wrap">
            <motion.div
              className="max-auth-card max-auth-card--tall"
              variants={cardVariants}
              initial="hidden"
              animate="visible"
            >
              <div className="max-auth-header">
                <h2 className="max-auth-title">Create your trial account</h2>
                <p className="max-auth-subtitle">
                  Register your organization to access the MaX plant maintenance platform
                </p>
              </div>

              <form onSubmit={handleSubmit} className="max-auth-form">
                {errors.submit ? (
                  <div className="max-auth-message max-auth-message--error">{errors.submit}</div>
                ) : null}

                <div className="max-auth-field">
                  <label className="max-auth-label" htmlFor="tenantCode">
                    Tenant code <span className="required">*</span>
                  </label>
                  <div className="max-auth-input-wrap">
                    <input
                      type="text"
                      id="tenantCode"
                      name="tenantCode"
                      value={formData.tenantCode}
                      readOnly
                      className="max-auth-input disabled-input"
                      placeholder="Auto-generated"
                    />
                  </div>
                  <span className="field-hint">Unique identifier is generated for your organization</span>
                </div>

                <div className="max-auth-field">
                  <label className="max-auth-label" htmlFor="tenantName">
                    Company name <span className="required">*</span>
                  </label>
                  <div className="max-auth-input-wrap">
                    <input
                      type="text"
                      id="tenantName"
                      name="tenantName"
                      value={formData.tenantName}
                      onChange={handleChange}
                      placeholder="Enter your company name"
                      required
                      className={`max-auth-input ${errors.tenantName ? "error" : ""}`}
                    />
                  </div>
                  {errors.tenantName ? <span className="field-error">{errors.tenantName}</span> : null}
                </div>

                <div className="max-auth-field">
                  <label className="max-auth-label" htmlFor="contactPerson">
                    Contact person
                  </label>
                  <div className="max-auth-input-wrap">
                    <input
                      type="text"
                      id="contactPerson"
                      name="contactPerson"
                      value={formData.contactPerson}
                      onChange={handleChange}
                      placeholder="Name of primary contact"
                      className="max-auth-input"
                    />
                  </div>
                </div>

                <div className="max-auth-field">
                  <label className="max-auth-label" htmlFor="email">
                    Email <span className="required">*</span>
                  </label>
                  <div className="max-auth-input-wrap">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="contact@company.com"
                      required
                      className={`max-auth-input ${errors.email ? "error" : ""}`}
                    />
                  </div>
                  {errors.email ? <span className="field-error">{errors.email}</span> : null}
                </div>

                <div className="max-auth-field">
                  <label className="max-auth-label" htmlFor="phone">
                    Phone
                  </label>
                  <div className="max-auth-input-wrap">
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 98765 43210"
                      className="max-auth-input"
                      autoComplete="tel"
                    />
                  </div>
                </div>

                <button type="submit" className="max-auth-submit" disabled={loading}>
                  {loading ? "Creating account…" : "Create account"}
                </button>
              </form>

              <div className="max-auth-footer-text">
                <span>Already have an account? </span>
                <Link to="/login" className="max-auth-inline-link">
                  Sign in
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
